import dayjs from 'dayjs'
import { useState } from 'react'
import { plural, t } from '@lingui/macro'
import { Appointments } from '@devexpress/dx-react-scheduler-material-ui'
import { styled } from '@mui/material/styles'
import { Typography, Stack, Box, Tooltip, Chip, IconButton } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import GroupIcon from '@mui/icons-material/Group'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'

import { useGetShiftTemplatesQuery } from '@/core/services/planning'

import { CalendarShiftData } from '../model/types'
import { useLingui } from '@lingui/react'
import { EditShiftTemplateDialog } from './EditShiftTemplateDialog'
import { ShiftTemplateDetails } from './ShiftTemplateDetails'

const StyledShiftTemplateAppointment = styled(Box, {
  shouldForwardProp: (prop) => !['isPast', 'isFuture', 'isDisabled'].includes(String(prop)),
})<{ isPast: boolean; isFuture: boolean; isDisabled?: boolean }>(({ isPast, isFuture, isDisabled = false, theme }) => ({
  height: '100%',
  backgroundColor: isDisabled ? 'rgba(189, 189, 189, 0.15)' : isPast ? 'rgba(224, 224, 224, 0.15)' : 'rgba(33, 150, 243, 0.1)',
  border: `2px dashed ${isDisabled ? '#9E9E9E' : isPast ? '#BDBDBD' : '#2196F3'}`,
  color: isDisabled ? '#9E9E9E' : isPast ? '#757575' : '#1976D2',
  borderRadius: theme.shape.borderRadius,
  opacity: isDisabled || isPast ? 0.7 : 1,
  zIndex: isDisabled || isPast ? 0 : 1,
  cursor: isDisabled || isPast ? 'not-allowed' : 'pointer',
  position: 'relative',
  transition: 'all 0.2s ease',
  padding: theme.spacing(0.75),
  '&:hover': {
    backgroundColor: isDisabled || isPast ? 'rgba(224, 224, 224, 0.2)' : 'rgba(33, 150, 243, 0.15)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: isDisabled
      ? 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(0,0,0,0.05) 5px, rgba(0,0,0,0.05) 10px)'
      : 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(33, 150, 243, 0.05) 10px, rgba(33, 150, 243, 0.05) 15px)',
    pointerEvents: 'none',
    borderRadius: 'inherit',
  },
  margin: theme.spacing(0.25, 0.5),
  minHeight: '80px',
  '&:not(:first-of-type)': {
    marginTop: -1,
  },
}))

function getRecurrenceText(data: CalendarShiftData) {
  if (!data.recurrenceRule) return t`No recurrence`

  const { recurrenceType, separationCount } = data.recurrenceRule

  if (recurrenceType === 'DAILY') {
    return t`Daily`
  }
  if (recurrenceType === 'WEEKLY') {
    return plural(separationCount, {
      one: 'Weekly',
      other: `Every ${separationCount} weeks`,
    })
  }

  return t`Custom recurrence`
}

type ShiftTemplateAppointmentProps = Omit<Appointments.AppointmentProps, 'data'> & {
  data: CalendarShiftData
  planningId: string
}

/**
 * Renders a shift template appointment in the calendar
 * Shows room, time, and required staff information with enhanced visual indicators
 */
export function ShiftTemplateAppointment({ data, planningId, ...rest }: ShiftTemplateAppointmentProps) {
  useLingui()
  const [openShiftTemplateDetails, setOpenShiftTemplateDetails] = useState(false)

  // Fetch the shift template data if we have a planningId and shiftTemplateId
  const { data: shiftTemplates } = useGetShiftTemplatesQuery(planningId)

  // Find the shift template in the fetched data
  const shiftTemplate = shiftTemplates?.find((template) => template._id === data.shiftTemplateId)

  const formattedStartTime = dayjs(data.startDate).format('HH:mm')
  const formattedEndTime = dayjs(data.endDate).format('HH:mm')
  const timeRange = `${formattedStartTime} - ${formattedEndTime}`

  if (!shiftTemplate) return null

  return (
    <>
      <Appointments.Appointment {...rest} data={data}>
        <StyledShiftTemplateAppointment
          isPast={data.isPast}
          isFuture={data.isFuture ?? false}
          isDisabled={data.isShiftCreated}
          onClick={() => {
            if (data.isPast || data.isShiftCreated) return
            setOpenShiftTemplateDetails(true)
          }}
        >
          <Box height="100%" padding={(theme) => theme.spacing(1)} overflow="visible">
            <Stack spacing={0.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {data.location}
                </Typography>
                <Box>
                  {data.isShiftCreated && <Chip label={t`Created`} size="small" color="primary" sx={{ height: '16px', fontSize: '0.6rem', mr: 0.5 }} />}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Tooltip title={timeRange}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTimeIcon fontSize="small" color="action" sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" color="text.secondary">
                      {timeRange}
                    </Typography>
                  </Box>
                </Tooltip>

                <Tooltip title={t`${data.requiredStaffCount} staff required`}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <GroupIcon fontSize="small" color="action" sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" color="text.secondary">
                      {data.requiredStaffCount}
                    </Typography>
                  </Box>
                </Tooltip>

                {data.recurrenceRule && (
                  <Tooltip title={getRecurrenceText(data)}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <EventRepeatIcon fontSize="small" color="action" sx={{ fontSize: '0.9rem' }} />
                      <Typography variant="caption" color="text.secondary">
                        {getRecurrenceText(data).substring(0, 10)}...
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Stack>
          </Box>
        </StyledShiftTemplateAppointment>
      </Appointments.Appointment>
      <ShiftTemplateDetails
        open={openShiftTemplateDetails}
        onClose={() => setOpenShiftTemplateDetails(false)}
        shiftTemplate={shiftTemplate}
        selectedStart={data.startDate}
        selectedEnd={data.endDate}
        planningId={planningId}
        isAdmin
      />
    </>
  )
}
